import { Suspense, useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { CustomizeTheme } from "./CustomizeTheme";
import { RouterProvider } from "react-router-dom";
import { router } from "./routes";
import { accountIndex, getUserDetails, getUserDetailsV2 } from "./redux/features/user/UserApi";
import { useAppDispatch } from "./redux/store";
import { clearPasswordSetupStatus, fetchPasswordSetupStatus } from "./redux/features/profile";
import { ThemeContextProvider, ThemeType } from "./theme";
import { ToastContainer } from "react-toastify";
import { PWAAuthModal } from "./components/PWA/PWAAuthModal";
// import { PWAInstallPrompt } from "./components/PWA/PWAInstallPrompt";
import { pwaAuthService, PWAUser } from "./services/pwaAuthService";
import { pwaService } from "./services/pwaService";

function App() {
    console.info("Application Running On", process.env.REACT_APP_ENV, process.env.REACT_APP_BACKEND_URL);

    const dispatch = useAppDispatch();
    const [showPWAAuth, setShowPWAAuth] = useState(false);
    const [currentUser, setCurrentUser] = useState<PWAUser | undefined>(undefined);

    useEffect(() => {
        let isActive = true;

        const bootstrapAccount = async () => {
            const result = await dispatch(accountIndex());

            if (!isActive) {
                return;
            }

            if (!accountIndex.fulfilled.match(result)) {
                dispatch(clearPasswordSetupStatus());
                return;
            }

            const actor = result.payload;
            const employeeId = actor?.os_emp_account_id?.toString();
            const isSuperUser = Number(actor?.is_super_user ?? actor?.isSuperUser ?? 0) === 1;
            const isValidEmployeeId = Boolean(employeeId && employeeId !== "0" && employeeId !== "-1");

            if (isValidEmployeeId && !isSuperUser) {
                dispatch(fetchPasswordSetupStatus({ employee_id: employeeId as string }));
            } else {
                dispatch(clearPasswordSetupStatus());
            }
        };

        bootstrapAccount();

        return () => {
            isActive = false;
        };
    }, [dispatch]);

    useEffect(() => {
        // Check if user should see PWA auth modal
        const checkPWAAuth = async () => {
            const storedUser = pwaAuthService.getStoredUser();
            const isPWAInstalled = pwaService.isPWAInstalled();
            
            if (storedUser && !isPWAInstalled) {
                // Show PWA auth modal for existing users who haven't installed the app
                setCurrentUser(storedUser);
                setShowPWAAuth(true);
            }
        };

        checkPWAAuth();
    }, []);

    // Disable pinch zoom in WebView (React Native WebView) for app-like experience
    useEffect(() => {
        if (typeof window !== "undefined" && (window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView) {
            const meta = document.querySelector('meta[name="viewport"]');
            if (meta) {
                meta.setAttribute(
                    "content",
                    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
                );
            }
        }
    }, []);

    const handlePWAAuthenticated = (user: PWAUser) => {
        setCurrentUser(user);
        setShowPWAAuth(false);
        // Store the user session
        pwaAuthService.storeUserSession(user, true);
    };

    const handlePWAAuthClose = () => {
        setShowPWAAuth(false);
    };

    // return (
    //   <Suspense fallback={<CircularProgress />}>
    //     <ThemeProvider theme={CustomizeTheme}>
    //       <RouterProvider router={router} />
    //     </ThemeProvider>
    //   </Suspense>
    // );

    return (
        <Suspense fallback={<CircularProgress />}>
            <ThemeContextProvider initialThemeType={ThemeType.DEFAULT}>
                <ToastContainer />
                <RouterProvider router={router} />
                
                {/* PWA Components */}
                {/* PWAInstallPrompt - Disabled as requested */}
                {/* <PWAInstallPrompt /> */}
                
                <PWAAuthModal
                    open={showPWAAuth}
                    onClose={handlePWAAuthClose}
                    onAuthenticated={handlePWAAuthenticated}
                    user={currentUser}
                />
            </ThemeContextProvider>
        </Suspense>
    );
}

export default App;
